import { useCallback, useState } from "react";

import type { ApiResult } from "../types/api";
import { toRequestError } from "../utils/apiError";

// Loading and error handling for a form that sends data.
// submit() resolves to the result on success and to null on failure (the error is stored).
export const useFormRequest = (networkMessage: string) => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const submit = useCallback(
        async <T extends ApiResult>(
            request: () => Promise<T>,
            failMessage: string
        ): Promise<T | null> => {
            setLoading(true);
            setError("");

            try {
                const result = await request();

                if (!result.success) {
                    setError(result.message || failMessage);
                    return null;
                }
                return result;
            } catch (err) {
                console.error("Request failed:", err);

                // The server answered with an error: show its message. No answer: network problem.
                const { message, status } = toRequestError(err);
                setError(status !== undefined ? message || failMessage : networkMessage);
                return null;
            } finally {
                setLoading(false);
            }
        },
        [networkMessage]
    );

    // setError is returned so the form can report its own validation errors
    return { loading, error, setError, submit };
};